/**
 * CreateAnnotationModal - Modal for creating new annotations.
 *
 * Supports creating:
 * - Issues (with category, priority, assignment)
 * - Self Notes (personal, only visible to creator)
 * - Role Comments (visible to specific roles)
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, StickyNote, MessageSquare, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useAnnotationStore } from '@/stores/annotationStore'
import { useCreateIssue, useCreateSelfNote, useCreateRoleComment } from '@/hooks/useAnnotations'
import type { AnnotationType, IssueCategory, IssuePriority } from '@/types/annotation'
import {
  ISSUE_CATEGORY_LABELS,
  ISSUE_PRIORITY_LABELS,
} from '@/types/annotation'
import { useCurrentStudy } from '@/stores/studyStore'

// Radix UI Select doesn't allow empty string values, so use a placeholder
const UNASSIGNED_VALUE = '__unassigned__'

export function CreateAnnotationModal() {
  const { toast } = useToast()
  const { studyId: urlStudyId } = useParams<{ studyId: string }>()

  // Store state
  const createModal = useAnnotationStore((state) => state.createModal)
  const closeCreateModal = useAnnotationStore((state) => state.closeCreateModal)
  const setCreateModalType = useAnnotationStore((state) => state.setCreateModalType)

  // Current study - Use URL param first (main domain), fallback to store (subdomain)
  const currentStudy = useCurrentStudy()
  const studyId = urlStudyId || currentStudy?.studyId

  // Form state - separate content for each type
  const [title, setTitle] = useState('')
  const [issueContent, setIssueContent] = useState('')
  const [noteContent, setNoteContent] = useState('')
  const [commentContent, setCommentContent] = useState('')
  const [category, setCategory] = useState<IssueCategory>('data_discrepancy')
  const [priority, setPriority] = useState<IssuePriority>('medium')
  const [assignedToRole, setAssignedToRole] = useState<string>('')

  // Mutations
  const createIssue = useCreateIssue(studyId)
  const createSelfNote = useCreateSelfNote(studyId)
  const createRoleComment = useCreateRoleComment(studyId)

  // Reset form when modal opens
  useEffect(() => {
    if (createModal.isOpen) {
      const prefilled = createModal.prefilledContent || ''
      setIssueContent(prefilled)
      setNoteContent(prefilled)
      setCommentContent(prefilled)
      setTitle('')
      setAssignedToRole('')

      // Set presets if provided
      if (createModal.presetCategory) {
        setCategory(createModal.presetCategory as IssueCategory)
      }
      if (createModal.presetPriority) {
        setPriority(createModal.presetPriority as IssuePriority)
      }
    }
  }, [createModal.isOpen, createModal.prefilledContent, createModal.presetCategory, createModal.presetPriority])

  // Get the content for the current type
  const getCurrentContent = () => {
    switch (createModal.type) {
      case 'issue':
        return issueContent
      case 'self_note':
        return noteContent
      case 'role_comment':
        return commentContent
      default:
        return ''
    }
  }

  const handleSubmit = async () => {
    const content = getCurrentContent()

    if (!content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Content is required',
        variant: 'destructive',
      })
      return
    }

    try {
      if (createModal.type === 'issue') {
        await createIssue.mutateAsync({
          datasetName: createModal.datasetName,
          recordKey: createModal.recordKey,
          fieldName: createModal.fieldName,
          content: issueContent,
          category,
          priority,
          assignedToRole: assignedToRole || undefined,
          valueAtCreation: createModal.fieldValue != null ? String(createModal.fieldValue) : undefined,
          sourceModuleKey: createModal.sourceModuleKey,
          sourcePageKey: createModal.sourcePageKey,
        })
        toast({
          title: 'Issue Created',
          description: 'The issue has been created successfully',
        })
      } else if (createModal.type === 'self_note') {
        await createSelfNote.mutateAsync({
          datasetName: createModal.datasetName,
          recordKey: createModal.recordKey,
          fieldName: createModal.fieldName,
          content: noteContent,
          sourceModuleKey: createModal.sourceModuleKey,
          sourcePageKey: createModal.sourcePageKey,
        })
        toast({
          title: 'Note Created',
          description: 'Your personal note has been saved',
        })
      } else if (createModal.type === 'role_comment') {
        if (!assignedToRole) {
          toast({
            title: 'Validation Error',
            description: 'Target role is required for role comments',
            variant: 'destructive',
          })
          return
        }
        await createRoleComment.mutateAsync({
          datasetName: createModal.datasetName,
          recordKey: createModal.recordKey,
          fieldName: createModal.fieldName,
          content: commentContent,
          targetRole: assignedToRole,
          sourceModuleKey: createModal.sourceModuleKey,
          sourcePageKey: createModal.sourcePageKey,
        })
        toast({
          title: 'Comment Created',
          description: `Comment sent to ${assignedToRole} role`,
        })
      }

      closeCreateModal()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create annotation',
        variant: 'destructive',
      })
    }
  }

  const isLoading = createIssue.isPending || createSelfNote.isPending || createRoleComment.isPending

  // Available roles (would come from API in real app)
  const availableRoles = ['Data Manager', 'Clinical Reviewer', 'Statistical Analyst', 'Medical Monitor']

  const getTypeIcon = (type: AnnotationType) => {
    switch (type) {
      case 'issue':
        return <AlertTriangle className="h-4 w-4" />
      case 'self_note':
        return <StickyNote className="h-4 w-4" />
      case 'role_comment':
        return <MessageSquare className="h-4 w-4" />
    }
  }

  return (
    <Dialog open={createModal.isOpen} onOpenChange={(open) => !open && closeCreateModal()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTypeIcon(createModal.type)}
            Create {createModal.type === 'issue' ? 'Issue' : createModal.type === 'self_note' ? 'Self Note' : 'Role Comment'}
          </DialogTitle>
          <DialogDescription>
            {createModal.type === 'issue' && 'Create an issue to track and resolve data problems'}
            {createModal.type === 'self_note' && 'Create a personal note (only visible to you)'}
            {createModal.type === 'role_comment' && 'Send a comment to a specific role'}
          </DialogDescription>
        </DialogHeader>

        {/* Data Context */}
        {(createModal.datasetName || createModal.fieldName) && (
          <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-lg">
            {createModal.datasetName && (
              <Badge variant="outline">Dataset: {createModal.datasetName}</Badge>
            )}
            {createModal.fieldName && (
              <Badge variant="secondary">Field: {createModal.fieldName}</Badge>
            )}
            {createModal.fieldValue != null && (
              <Badge variant="secondary">Value: {String(createModal.fieldValue)}</Badge>
            )}
          </div>
        )}

        {/* Type Selector */}
        <Tabs value={createModal.type} onValueChange={(v) => setCreateModalType(v as AnnotationType)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="issue" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Issue
            </TabsTrigger>
            <TabsTrigger value="self_note" className="flex items-center gap-2">
              <StickyNote className="h-4 w-4" />
              Self Note
            </TabsTrigger>
            <TabsTrigger value="role_comment" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Role Comment
            </TabsTrigger>
          </TabsList>

          {/* Issue Form */}
          <TabsContent value="issue" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title (Optional)</Label>
              <Input
                id="title"
                placeholder="Brief summary of the issue"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={category} onValueChange={(v) => setCategory(v as IssueCategory)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ISSUE_CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as IssuePriority)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ISSUE_PRIORITY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assignedTo">Assign to Role (Optional)</Label>
              <Select value={assignedToRole || UNASSIGNED_VALUE} onValueChange={(v) => setAssignedToRole(v === UNASSIGNED_VALUE ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role to assign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
                  {availableRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="issue-content">Description</Label>
              <Textarea
                id="issue-content"
                placeholder="Describe the issue in detail..."
                className="min-h-[120px]"
                value={issueContent}
                onChange={(e) => setIssueContent(e.target.value)}
              />
            </div>
          </TabsContent>

          {/* Self Note Form */}
          <TabsContent value="self_note" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="note-content">Note</Label>
              <Textarea
                id="note-content"
                placeholder="Write your personal note..."
                className="min-h-[150px]"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This note is private and will only be visible to you.
              </p>
            </div>
          </TabsContent>

          {/* Role Comment Form */}
          <TabsContent value="role_comment" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="targetRole">Send to Role</Label>
              <Select value={assignedToRole || undefined} onValueChange={setAssignedToRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment-content">Comment</Label>
              <Textarea
                id="comment-content"
                placeholder="Write your comment..."
                className="min-h-[120px]"
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This comment will be visible to users with the selected role.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={closeCreateModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !getCurrentContent().trim()}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {createModal.type === 'issue' ? 'Create Issue' : createModal.type === 'self_note' ? 'Save Note' : 'Send Comment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateAnnotationModal
